/**
 * Account Delete Dialog Component
 *
 * Confirmation dialog for account deletion.
 * Requires user to type "DELETE" to confirm.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';

/**
 * Props for AccountDeleteDialog component
 */
export interface AccountDeleteDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
}

/**
 * Confirmation text required to delete account
 */
const CONFIRMATION_TEXT = 'DELETE';

/**
 * Account delete dialog component
 */
export const AccountDeleteDialog = ({ isOpen, onClose }: AccountDeleteDialogProps) => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isConfirmed = confirmText === CONFIRMATION_TEXT;

  /**
   * Handle delete account
   */
  const handleDelete = async () => {
    if (!isConfirmed) return;

    try {
      setError(null);
      setIsDeleting(true);

      const functions = getFirebaseFunctions();
      const deleteAccount = httpsCallable(functions, 'deleteAccount');

      await deleteAccount();

      // Sign out after deletion
      await signOut();

      // Redirect to login page
      navigate('/login', { replace: true });
    } catch (deleteError) {
      const errorMessage =
        deleteError instanceof Error ? deleteError.message : 'Failed to delete account';
      console.error('Delete account error:', deleteError);
      setError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Handle dialog close
   */
  const handleClose = () => {
    if (isDeleting) return;
    setConfirmText('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
        }}
      >
        {/* Dialog */}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '16px',
            }}
          >
            <h2
              style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#991b1b',
              }}
            >
              Delete Account
            </h2>
            <button
              onClick={handleClose}
              disabled={isDeleting}
              style={{
                background: 'none',
                border: 'none',
                cursor: isDeleting ? 'not-allowed' : 'pointer',
                fontSize: '1.5rem',
                color: '#9ca3af',
                lineHeight: 1,
              }}
            >
              &times;
            </button>
          </div>

          {/* Warning */}
          <div
            style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
            }}
          >
            <p
              style={{
                color: '#991b1b',
                fontSize: '0.875rem',
                lineHeight: 1.5,
              }}
            >
              This action is <strong>permanent</strong> and cannot be undone. All your data
              including:
            </p>
            <ul
              style={{
                marginTop: '8px',
                marginLeft: '16px',
                color: '#991b1b',
                fontSize: '0.875rem',
              }}
            >
              <li>Your profile information</li>
              <li>Your gym memberships</li>
              <li>Your climb logs</li>
              <li>Your grade submissions</li>
              <li>Your quality ratings</li>
            </ul>
            <p
              style={{
                marginTop: '8px',
                color: '#991b1b',
                fontSize: '0.875rem',
              }}
            >
              will be <strong>permanently deleted</strong>.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div
              style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                color: '#991b1b',
                fontSize: '0.875rem',
              }}
            >
              {error}
            </div>
          )}

          {/* Confirmation Input */}
          <div
            style={{
              marginBottom: '16px',
            }}
          >
            <label
              htmlFor="confirm-delete"
              style={{
                display: 'block',
                marginBottom: '8px',
                color: '#374151',
                fontSize: '0.875rem',
              }}
            >
              Type <strong>{CONFIRMATION_TEXT}</strong> to confirm:
            </label>
            <input
              id="confirm-delete"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={isDeleting}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
              placeholder="Type DELETE"
              autoComplete="off"
            />
          </div>

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
            }}
          >
            <button
              onClick={handleClose}
              disabled={isDeleting}
              style={{
                padding: '10px 20px',
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#374151',
                cursor: isDeleting ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={!isConfirmed || isDeleting}
              style={{
                padding: '10px 20px',
                backgroundColor: isConfirmed ? '#dc2626' : '#fca5a5',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'white',
                cursor: isConfirmed && !isDeleting ? 'pointer' : 'not-allowed',
                opacity: isDeleting ? 0.7 : 1,
              }}
            >
              {isDeleting ? 'Deleting...' : 'Delete Account'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
